<div class="mapper-loading-shell" *ngIf="showInitialLoader">
	<div class="mapper-loading-card">
		<div class="mapper-loading-hero">
			<div class="mapper-loading-stage">Preparing mapper workspace</div>
			<h3>Loading mapper</h3>
			<p>{{ pageLoadingDetail }}</p>
			<div class="mapper-loading-current" aria-live="polite">
				<strong>{{ pageLoadingMessage }}</strong>
				<span>{{ pageLoadingProgressPercent }}%</span>
			</div>
			<div class="mapper-loading-progress" aria-hidden="true">
				<div class="mapper-loading-progress-bar" [style.width.%]="pageLoadingProgressPercent"></div>
			</div>
		</div>
	</div>
</div>

<div class="mapper-error-shell" *ngIf="showMapperLoadError">
	<div class="mapper-error-card">
		<div class="mapper-error-icon">!</div>
		<div class="mapper-error-copy">
			<div class="section-kicker">Mapper error</div>
			<h3>{{ mapperLoadErrorTitle || 'Mapper could not be loaded' }}</h3>
			<p>{{ mapperLoadErrorMessage }}</p>
			<div class="mapper-error-actions">
				<button type="button" class="action-btn action-btn-primary" (click)="reloadMapperMetadata()" [disabled]="isPageLoading">Retry</button>
				<button type="button" class="action-btn action-btn-ghost" (click)="clearMetadataCache(); reloadMapperMetadata()" [disabled]="isPageLoading">Clear cache and retry</button>
			</div>
		</div>
	</div>
</div>

<div class="page mapper-page" *ngIf="visibleConfiguration as config">
	<section class="workbench-grid" [class.is-basic-mode]="showBasicMapperScreen">
		<div class="mapping-column">
			<section class="workbench-card mapper-hero-card">
				<div class="mapper-hero-header">
					<div class="mapper-hero-topline">
						<div class="mapper-hero-copy">
							<div class="section-kicker">Field Mapping</div>
							<h2>{{ config.sourceSchema.systemName }} {{ config.sourceSchema.entityName }} → {{ config.targetSchema.systemName }} {{ config.targetSchema.entityName }}</h2>
						</div>

						<div class="mapper-profile-slot">
							<div class="profile-menu in-toolbar" [class.open]="profileMenuOpen">
								<button type="button" class="action-btn profile-menu-trigger" data-cy="mapper-profile-menu-button" [class.is-selected]="hasSelectedProfile" (click)="toggleProfileMenu()" [disabled]="isPageLoading">
								<span class="profile-menu-trigger-label">{{ currentProfileLabel }}</span>
								<span class="profile-menu-trigger-caret">{{ profileMenuOpen ? 'Close' : selectedProfileIsActive ? 'Active profile' : hasSelectedProfile ? 'Reviewing profile' : 'Profile' }}</span>
							</button>

								<div class="profile-menu-panel" *ngIf="profileMenuOpen">
									<div class="profile-menu-section">
										<div class="profile-menu-title">Current profile</div>
										<div class="profile-current-card">
											<strong>{{ currentProfileLabel }}</strong>
											<span>{{ currentProfileSummaryText }}</span>
											<span *ngIf="activeProfileName && !selectedProfileIsActive">Active runtime profile: {{ activeProfileName }}</span>
										</div>
										<div class="profile-cache-actions" *ngIf="hasSelectedProfile">
											<button type="button" class="action-btn action-btn-primary" data-cy="mapper-set-active-profile-button" (click)="setSelectedProfileActive()" [disabled]="!canSetSelectedProfileActive">{{ selectedProfileIsActive ? 'Active profile' : 'Set active' }}</button>
										</div>
									</div>

									<div class="profile-menu-section">
										<div class="profile-menu-title">Saved profiles</div>
										<button type="button" class="profile-option" [attr.data-cy]="'mapper-profile-option-' + profile.name" [class.is-selected]="profile.name === selectedProfileName" *ngFor="let profile of availableProfiles" (click)="selectProfile(profile.name)" [disabled]="isPageLoading">
										<span>{{ profile.name }}</span>
										<span class="profile-option-meta">{{ profile.isActive ? 'Active' : profile.name === selectedProfileName ? 'Selected' : formatTimestamp(profile.lastModificationDate || profile.creationDate) }}</span>
									</button>
										<div class="profile-empty" *ngIf="!availableProfiles.length">No saved profiles yet.</div>
									</div>

									<div class="profile-menu-section">
										<div class="profile-menu-title">Templates for this link type</div>
										<div class="profile-current-card profile-cache-card">
											<strong>{{ templateScopeSummary }}</strong>
											<span>{{ templateScopeTechnicalSummary }}</span>
										</div>
										<button type="button" class="profile-option profile-option-rich" [attr.data-cy]="'mapper-template-option-' + template.name" *ngFor="let template of availableTemplates" (click)="applyTemplate(template.name)" [disabled]="isPageLoading">
											<span class="profile-option-main">
												<strong>{{ template.name }}</strong>
												<small>{{ template.summary || 'Reusable mapper setup for this link type.' }}</small>
											</span>
											<span class="profile-option-meta">Apply</span>
										</button>
										<div class="profile-empty" *ngIf="!availableTemplates.length">No templates are defined for this exact FROM/TO link type and entity pair.</div>
										<div class="profile-cache-actions profile-template-actions" *ngIf="canManageTemplates">
											<div class="profile-menu-title">Admin template management</div>
											<input class="grid-search profile-name-input" data-cy="mapper-template-name-input" type="text" [value]="templateName" (input)="templateName = $any($event.target).value" placeholder="Template name" />
											<button type="button" class="action-btn" data-cy="mapper-save-template-button" (click)="saveCurrentMappingAsTemplate()" [disabled]="isPageLoading || !templateName.trim()">{{ saveTemplateButtonLabel }}</button>
										</div>
									</div>

									<div class="profile-menu-section profile-menu-create">
										<div class="profile-menu-title">Create or update</div>
										<input class="grid-search profile-name-input" data-cy="mapper-profile-name-input" type="text" [value]="profileName" (input)="profileName = $any($event.target).value" placeholder="Profile name" />
										<div class="profile-cache-actions">
											<button type="button" class="action-btn action-btn-primary" data-cy="mapper-save-profile-menu-button" (click)="saveMapping()" [disabled]="!canSaveMapping">{{ saveMappingProfileButtonLabel }}</button>
											<button type="button" class="action-btn action-btn-ghost" data-cy="mapper-discard-changes-button" (click)="discardMappingChanges()" [disabled]="!canDiscardMappingChanges">{{ discardMappingChangesButtonLabel }}</button>
										</div>
									</div>

									<div class="profile-menu-section profile-menu-create">
										<div class="profile-menu-title">Metadata cache</div>
										<div class="profile-current-card profile-cache-card">
											<strong>Schema and entity metadata</strong>
											<span>Useful during testing when you want to avoid reloading mapper models on every open.</span>
											<div class="profile-cache-status-row">
												<span class="state-pill" [attr.data-tone]="metadataCachingEnabled ? 'success' : 'muted'">{{ metadataCacheStatusLabel }}</span>
												<span class="profile-cache-meta">{{ hasCachedMetadata ? 'Cached data available' : 'No cached data yet' }}</span>
											</div>
										</div>
										<div class="profile-cache-actions">
											<button type="button" class="action-btn" data-cy="mapper-use-metadata-cache-button" (click)="toggleMetadataCaching(true)" [disabled]="isPageLoading || metadataCachingEnabled">Use cache</button>
											<button type="button" class="action-btn" data-cy="mapper-disable-metadata-cache-button" (click)="toggleMetadataCaching(false)" [disabled]="isPageLoading || !metadataCachingEnabled">Disable cache</button>
											<button type="button" class="action-btn action-btn-ghost" data-cy="mapper-clear-metadata-cache-button" (click)="clearMetadataCache()" [disabled]="isPageLoading || !hasCachedMetadata">Clear cache</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div class="mapper-hero-toolbar">
						<div class="mapper-screen-switch" role="group" aria-label="Mapper mode">
							<button type="button" class="mapper-screen-switch-btn" data-cy="mapper-basic-mode-button" [class.is-selected]="showBasicMapperScreen" [attr.aria-pressed]="showBasicMapperScreen" (click)="setMapperScreenMode('basic')">Basic</button>
							<button type="button" class="mapper-screen-switch-btn" data-cy="mapper-advanced-mode-button" [class.is-selected]="showAdvancedMapperScreen" [attr.aria-pressed]="showAdvancedMapperScreen" (click)="setMapperScreenMode('advanced')">Advanced</button>
						</div>

						<div class="mapping-actions">
							<div class="profile-menu in-toolbar test-run-menu" [class.open]="testRunMenuOpen" *ngIf="showTransferByIdTestRunFlow; else simpleTestRunButton">
								<button type="button" class="action-btn profile-menu-trigger" data-cy="mapper-test-run-menu-button" (click)="toggleTestRunMenu()" [disabled]="isPageLoading || isTestRunStarting || isLinkRunning">
									<span class="profile-menu-trigger-label">{{ testRunMenuLabel }}</span>
									<span class="profile-menu-trigger-caret">{{ testRunMenuHint }}</span>
								</button>

								<div class="profile-menu-panel" *ngIf="testRunMenuOpen">
									<div class="profile-menu-section">
										<div class="profile-menu-title">Transfer by ID</div>
										<input class="grid-search profile-name-input" data-cy="mapper-transfer-id-input" type="text" [value]="transferByIdDraftValue" (input)="transferByIdDraftValue = $any($event.target).value" [placeholder]="transferByIdPlaceholder" />
										<div class="profile-current-card">
											<strong>{{ transferByKeySetting?.displayName }}</strong>
										</div>
									</div>

									<div class="profile-cache-actions">
										<button type="button" class="action-btn" data-cy="mapper-apply-transfer-id-button" (click)="applyTransferByKeySetting()" [disabled]="!canApplyTransferByKeySetting">{{ applyTransferByKeyButtonLabel }}</button>
										<button type="button" class="action-btn action-btn-primary" data-cy="mapper-run-test-button" (click)="runMapperTestLink()" [disabled]="!canRunTestLink" [attr.title]="testRunButtonTitle">{{ testRunButtonLabel }}</button>
									</div>
								</div>
							</div>
							<div class="profile-menu in-toolbar admin-run-menu" [class.open]="adminRunMenuOpen">
								<button type="button" class="action-btn profile-menu-trigger" data-cy="mapper-diagnostics-menu-button" (click)="toggleAdminRunMenu()" [disabled]="isPageLoading">
									<span class="profile-menu-trigger-label">Diagnostics</span>
									<span class="profile-menu-trigger-caret">{{ adminRunToolsToggleLabel }}</span>
								</button>

								<div class="profile-menu-panel" *ngIf="adminRunMenuOpen">
									<div class="profile-menu-section">
										<div class="profile-menu-title">Saved test run</div>
										<div class="profile-current-card" data-cy="mapper-baseline-status-card">
											<strong>{{ hasSavedBaselineTestRun ? 'Baseline saved' : 'No saved baseline' }}</strong>
											<span>{{ savedBaselineSummaryText }}</span>
										</div>
										<div class="profile-cache-actions">
											<button type="button" class="action-btn" data-cy="mapper-save-baseline-button" (click)="saveLatestRunAsBaseline()" [disabled]="!canSaveLatestRunAsBaseline">{{ saveLatestRunAsBaselineButtonLabel }}</button>
											<button type="button" class="action-btn" *ngIf="hasSavedBaselineTestRun" (click)="clearSavedBaseline()">Clear saved run</button>
										</div>
									</div>

									<div class="profile-menu-section">
										<div class="profile-menu-title">Artifact capture</div>
										<div class="profile-current-card profile-cache-card">
											<strong>{{ captureArtifactsForSession ? 'Enabled in this browser' : 'Disabled by default' }}</strong>
											<span>{{ captureArtifactsSummaryText }}</span>
										</div>
										<div class="profile-cache-actions">
											<button type="button" class="action-btn" data-cy="mapper-toggle-artifact-capture-button" [class.action-btn-primary]="!captureArtifactsForSession" (click)="toggleCaptureArtifactsForSession()">{{ captureArtifactsButtonLabel }}</button>
										</div>
									</div>

									<div class="profile-menu-section" *ngIf="hasSavedBaselineTestRun">
										<div class="profile-menu-title">Compare another run</div>
										<input class="grid-search profile-name-input" data-cy="mapper-comparison-session-input" type="text" [value]="comparisonSessionId" (input)="onComparisonSessionIdChanged($any($event.target).value)" placeholder="Session id" />
										<div class="profile-cache-actions">
											<button type="button" class="action-btn action-btn-primary" data-cy="mapper-compare-baseline-button" (click)="compareRunAgainstSavedBaseline()" [disabled]="!canCompareAnotherRunAgainstSavedBaseline">{{ compareRunButtonLabel }}</button>
										</div>
										<div class="compare-run-loading" *ngIf="isLoadingComparisonRun">
											<span class="spinner spinner-sm" aria-hidden="true"></span>
											<span>Loading selected run and preparing comparison...</span>
										</div>
										<div class="profile-current-card profile-cache-card">
											<strong>Comparison</strong>
											<span>{{ savedRunComparisonSummaryText }}</span>
										</div>
									</div>

								</div>
							</div>
							<button type="button" class="action-btn" data-cy="mapper-toggle-metadata-cache-button" [class.is-selected]="metadataCachingEnabled" (click)="toggleMetadataCaching(!metadataCachingEnabled)" [disabled]="isPageLoading" [attr.title]="metadataCachingEnabled ? 'Disable mapper metadata cache' : 'Enable mapper metadata cache'">Cache: {{ metadataCachingEnabled ? 'On' : 'Off' }}</button>
							<ng-template #simpleTestRunButton>
							<button type="button" class="action-btn action-btn-primary" data-cy="mapper-run-test-button" (click)="runMapperTestLink()" [disabled]="!canRunTestLink" [attr.title]="testRunButtonTitle">{{ testRunButtonLabel }}</button>
							</ng-template>
							<button type="button" class="action-btn" data-cy="mapper-reload-button" (click)="reloadMapperMetadata()" [disabled]="isPageLoading" *ngIf="showAdvancedMapperScreen">Reload</button>
							<button type="button" class="action-btn" data-cy="mapper-validate-button" (click)="validateMappings()" [disabled]="isPageLoading" *ngIf="showAdvancedMapperScreen">Validate</button>
							<button type="button" class="action-btn" data-cy="mapper-preview-button" (click)="previewMappings()" [disabled]="isPageLoading" *ngIf="showAdvancedMapperScreen">Preview</button>
							<button type="button" class="action-btn" data-cy="mapper-guide-button" (click)="mapperDocumentationOpen = true" *ngIf="showAdvancedMapperScreen">Guide</button>
							<button type="button" class="action-btn action-btn-ghost" data-cy="mapper-reset-unsaved-button" (click)="discardMappingChanges()" [disabled]="!canDiscardMappingChanges">{{ resetUnsavedMappingsButtonLabel }}</button>
							<button type="button" class="action-btn action-btn-primary" data-cy="mapper-save-profile-button" (click)="saveMapping()" [disabled]="!canSaveMapping">{{ saveMappingButtonLabel }}</button>
						</div>
					</div>
				</div>
			</section>

			<section class="workbench-card mapper-test-run-results-card" data-cy="mapper-test-run-results-card" *ngIf="showLatestTestRunCard">
				<div class="mapper-test-run-results-head">
					<div>
						<div class="section-kicker">Latest test run</div>
					<h3 data-cy="mapper-latest-test-run-headline">{{ latestTestRunHeadline }}</h3>
					<p data-cy="mapper-active-test-run-session" *ngIf="activeTestRunSessionId">Session {{ activeTestRunSessionId }}</p>
					</div>
					<span class="state-pill" data-cy="mapper-latest-test-run-state" [attr.data-tone]="latestTestRunStateTone">{{ latestTestRunStateLabel }}</span>
				</div>

				<div class="mapper-test-run-results-loading" *ngIf="isLoadingLatestTestRunResult">
					<span class="spinner spinner-sm"></span>
					<span>Loading session results...</span>
				</div>

				<div class="mapper-run-progress" *ngIf="hasMapperRunProgress && !isLoadingLatestTestRunResult">
					<div class="mapper-run-progress-head">
						<div class="mapper-run-progress-stage-copy">
							<span class="mapper-run-progress-stage-kicker">{{ mapperRunStageFlowPositionLabel }}</span>
							<strong>{{ mapperRunProgressDisplayStage }}</strong>
						</div>
						<div class="mapper-run-progress-metrics" *ngIf="mapperRunProgressTotalCount > 0">
							<span class="mapper-run-progress-metric-value">{{ mapperRunProgressProcessedCount }}/{{ mapperRunProgressTotalCount }}</span>
							<span class="mapper-run-progress-metric-label">{{ mapperRunProgressCountLabel }}</span>
						</div>
					</div>
					<div class="mapper-run-stage-flow" aria-hidden="true">
						<div class="mapper-run-stage-flow-steps">
							<div class="mapper-run-stage-flow-step" *ngFor="let stage of mapperRunStageFlow; let i = index" [class.is-active]="i === mapperRunStageFlowIndex" [class.is-complete]="i < mapperRunStageFlowIndex" [class.is-upcoming]="i > mapperRunStageFlowIndex">
								<div class="mapper-run-stage-flow-dot">
									<span class="mapper-run-stage-flow-dot-content" [class.is-check]="i < mapperRunStageFlowIndex">{{ i < mapperRunStageFlowIndex ? '✓' : i + 1 }}</span>
								</div>
								<div class="mapper-run-stage-flow-label">{{ stage.label }}</div>
							</div>
						</div>
					</div>
					<div class="mapper-run-progress-copy" *ngIf="mapperRunProgressDisplayMessage">{{ mapperRunProgressDisplayMessage }}</div>
				</div>

				<div class="mapper-test-run-results-summary" *ngIf="hasLatestTestRunResult && !isLoadingLatestTestRunResult">
					<div class="mapper-test-run-summary-item" [attr.data-cy]="'mapper-test-run-summary-' + item.label" *ngFor="let item of latestTestRunSummaryItems">
						<span>{{ item.label }}</span>
						<strong [attr.data-tone]="item.tone">{{ item.value }}</strong>
					</div>
				</div>

				<div class="mapper-test-run-expected-log" data-cy="mapper-test-run-comparison-log" *ngIf="hasSavedRunComparisonLog">
					<div class="mapper-test-run-expected-log-head">
						<strong>Saved run vs selected run</strong>
						<span>Comparison between the saved baseline run and the session loaded by id.</span>
					</div>
					<div class="mapper-test-run-expected-log-list">
						<div class="mapper-test-run-expected-log-item" *ngFor="let item of savedRunComparisonLog">
							<div class="mapper-test-run-expected-log-meta">
								<strong>{{ item.label }}</strong>
								<span class="state-pill" [attr.data-tone]="getMapperExpectationComparisonTone(item.status)">{{ item.status }}</span>
							</div>
							<div class="mapper-test-run-expected-log-values" *ngIf="item.type !== 'artifact'; else savedRunArtifactDiffTemplate">
								<div>
									<span>Saved</span>
									<code>{{ item.saved || 'n/a' }}</code>
								</div>
								<div>
									<span>Current</span>
									<code>{{ item.current || 'n/a' }}</code>
								</div>
							</div>
							<ng-template #savedRunArtifactDiffTemplate>
								<div class="mapper-run-diff-block">
									<div class="mapper-run-diff-head">
										<span>Path</span>
										<span>Saved</span>
										<span>Current</span>
									</div>
									<div class="mapper-run-diff-body">
										<div class="mapper-run-diff-row" *ngFor="let entry of item.diffEntries || []">
											<div class="mapper-run-diff-path-cell" [attr.data-tone]="getSavedRunDiffTone(entry.status)">
												<code>{{ entry.path }}</code>
											</div>
											<div class="mapper-run-diff-cell" [attr.data-tone]="getSavedRunDiffTone(entry.status)">
												<code>{{ entry.savedValue || ' ' }}</code>
											</div>
											<div class="mapper-run-diff-cell" [attr.data-tone]="getSavedRunDiffTone(entry.status)">
												<code>{{ entry.currentValue || ' ' }}</code>
											</div>
										</div>
									</div>
								</div>
							</ng-template>
							<div class="mapper-test-run-expected-log-notes" *ngIf="item.notes">{{ item.notes }}</div>
						</div>
					</div>
				</div>

				<div class="mapper-test-run-results-empty" *ngIf="!hasLatestTestRunResult && !isLoadingLatestTestRunResult">
					Exact mapper success and failure messages for the run will appear here.
				</div>

				<app-mapper-artifact-trace
					[items]="artifactTraceItems"
					[showUnmappedFields]="showUnmappedArtifactFields"
					(toggleUnmappedFields)="toggleUnmappedArtifactFields()">
				</app-mapper-artifact-trace>

				<div class="mapper-test-run-message-list" *ngIf="latestTestRunMessages.length">
					<div class="mapper-test-run-message-item" [attr.data-cy]="'mapper-test-run-message-' + entry.jobLogId" *ngFor="let entry of latestTestRunMessages">
						<div class="mapper-test-run-message-meta">
							<span class="state-pill" [attr.data-tone]="entry.jobLogType === 1 ? 'success' : entry.jobLogType === 6 ? 'info' : 'danger'">{{ entry.jobLogTypeName }}</span>
							<span *ngIf="entry.creationDate">{{ formatTimestamp(entry.creationDate) }}</span>
						</div>
						<div class="mapper-test-run-message-copy">{{ entry.message }}</div>
					</div>
				</div>
			</section>

			<section class="mapper-basic-page" data-cy="mapper-basic-screen" *ngIf="showBasicMapperScreen">
				<aside class="mapper-basic-side-rail">
					<div class="mapper-basic-link-card">
						<strong>{{ config.sourceSchema.systemName }} to {{ config.targetSchema.systemName }}</strong>
						<span>{{ config.sourceSchema.entityName }} mapping</span>
						<small>{{ selectedProfileIsActive ? 'Active profile ready' : hasSelectedProfile ? 'Profile selected' : 'Setup in progress' }}</small>
					</div>

					<div class="mapper-basic-stepper">
						<div class="mapper-basic-step is-complete"><strong>1</strong><span><b>Setup</b><small>Connection and access</small></span></div>
						<div class="mapper-basic-step is-active"><strong>2</strong><span><b>Template</b><small>Choose mapping setup</small></span></div>
						<div class="mapper-basic-step"><strong>3</strong><span><b>Test</b><small>Verify with sample data</small></span></div>
						<div class="mapper-basic-step"><strong>4</strong><span><b>Activate</b><small>Save and use</small></span></div>
					</div>

					<div class="mapper-basic-help-card">
						<strong>Need a different setup?</strong>
						<span>Open the advanced editor to adjust fields or create a custom template.</span>
						<button type="button" class="action-btn" data-cy="mapper-open-advanced-editor-button" (click)="setMapperScreenMode('advanced')">Open advanced editor</button>
					</div>
				</aside>

				<main class="mapper-basic-template-main">
					<div class="mapper-basic-main-head">
						<div>
							<h3>Choose a template</h3>
							<p>Templates are ready-made mappings for this exact link type. Pick one and check the preview before applying it.</p>
						</div>
					</div>

					<div class="mapper-basic-template-toolbar">
						<input class="grid-search mapper-basic-template-search" type="text" [value]="templateSearch" (input)="templateSearch = $any($event.target).value" placeholder="Search templates..." />
						<button type="button" class="action-btn" (click)="setMapperScreenMode('advanced')">Create custom template</button>
					</div>

					<div class="mapper-basic-template-list-large" *ngIf="hasTemplateSearchResults; else mapperBasicNoTemplateResults">
						<button type="button" class="mapper-basic-template-card" [attr.data-cy]="'mapper-basic-template-' + template.name" [class.is-selected]="template.name === selectedTemplateName" *ngFor="let template of filteredAvailableTemplates; let i = index" (click)="selectTemplate(template.name)" [disabled]="isPageLoading">
							<span class="mapper-basic-template-radio" aria-hidden="true"></span>
							<span class="mapper-basic-template-card-copy">
								<strong>{{ template.name }}</strong>
								<small>{{ template.summary || 'Reusable mapper setup for this link type.' }}</small>
							</span>
							<span class="mapper-basic-recommended" *ngIf="i === 0">Recommended</span>
							<span class="mapper-basic-field-pill">{{ template.mappedFieldCount || 0 }} fields</span>
							<span class="mapper-basic-preview-button">Details</span>
						</button>
					</div>
					<ng-template #mapperBasicNoTemplateResults>
						<div class="mapper-basic-empty mapper-basic-empty-large">No templates match your search for this link type.</div>
					</ng-template>

					<section class="mapper-basic-template-detail-panel" *ngIf="selectedTemplate as template">
						<div class="mapper-basic-template-detail-head">
							<div>
								<strong>Template details</strong>
								<span>{{ template.name }}. See what gets used for create, update, writeback, and runtime messages.</span>
							</div>
							<span class="mapper-basic-field-pill">{{ selectedTemplateFieldCountLabel }}</span>
						</div>

						<div class="mapper-basic-template-loading" *ngIf="isLoadingSelectedTemplateDefinition"><span class="spinner spinner-sm"></span><span>Loading template details...</span></div>
						<div class="mapper-basic-template-error" *ngIf="selectedTemplateDefinitionError">{{ selectedTemplateDefinitionError }}</div>

						<div class="mapper-basic-mini-grid" *ngIf="selectedTemplateDefinition && !isLoadingSelectedTemplateDefinition">
							<div class="mapper-basic-mini-grid-tabs" role="tablist" aria-label="Template detail sections">
								<button type="button" class="mapper-basic-mini-grid-tab" data-cy="mapper-basic-template-detail-tab-create" [class.is-selected]="selectedTemplateMiniGridTab === 'create'" [attr.aria-selected]="selectedTemplateMiniGridTab === 'create'" (mousedown)="$event.preventDefault(); $event.stopPropagation(); setSelectedTemplateMiniGridTab('create')" (click)="$event.stopPropagation(); setSelectedTemplateMiniGridTab('create')"><span>Create</span><strong>{{ selectedTemplateMiniGridSections[0].count }}</strong></button>
								<button type="button" class="mapper-basic-mini-grid-tab" data-cy="mapper-basic-template-detail-tab-update" [class.is-selected]="selectedTemplateMiniGridTab === 'update'" [attr.aria-selected]="selectedTemplateMiniGridTab === 'update'" (mousedown)="$event.preventDefault(); $event.stopPropagation(); setSelectedTemplateMiniGridTab('update')" (click)="$event.stopPropagation(); setSelectedTemplateMiniGridTab('update')"><span>Update</span><strong>{{ selectedTemplateMiniGridSections[1].count }}</strong></button>
								<button type="button" class="mapper-basic-mini-grid-tab" data-cy="mapper-basic-template-detail-tab-writeback" [class.is-selected]="selectedTemplateMiniGridTab === 'writeback'" [attr.aria-selected]="selectedTemplateMiniGridTab === 'writeback'" (mousedown)="$event.preventDefault(); $event.stopPropagation(); setSelectedTemplateMiniGridTab('writeback')" (click)="$event.stopPropagation(); setSelectedTemplateMiniGridTab('writeback')"><span>Writeback</span><strong>{{ selectedTemplateMiniGridSections[2].count }}</strong></button>
								<button type="button" class="mapper-basic-mini-grid-tab" data-cy="mapper-basic-template-detail-tab-messages" [class.is-selected]="selectedTemplateMiniGridTab === 'messages'" [attr.aria-selected]="selectedTemplateMiniGridTab === 'messages'" (mousedown)="$event.preventDefault(); $event.stopPropagation(); setSelectedTemplateMiniGridTab('messages')" (click)="$event.stopPropagation(); setSelectedTemplateMiniGridTab('messages')"><span>Messages</span><strong>{{ selectedTemplateMiniGridSections[3].count }}</strong></button>
							</div>

							<div class="mapper-basic-mini-grid-table" *ngIf="selectedTemplateActiveMiniGridSection as section">
								<div class="mapper-basic-mini-grid-active-title">{{ section.title }} fields</div>
								<div class="mapper-basic-mini-grid-table-head">
									<span>{{ section.title === 'Writeback' ? 'Target result' : section.title === 'Messages' ? 'Event' : 'Source value' }}</span>
									<span>{{ section.title === 'Messages' ? 'Type' : 'Rule' }}</span>
									<span>{{ section.title === 'Writeback' ? 'Source field' : section.title === 'Messages' ? 'Message' : 'Target field' }}</span>
								</div>
								<div class="mapper-basic-mini-grid-empty" *ngIf="!section.rows.length">{{ section.emptyText }}</div>
								<div class="mapper-basic-mini-grid-row" [class.is-link-key]="row.isLinkKey" *ngFor="let row of section.rows">
									<span>{{ row.left }}</span>
									<small>{{ row.middle }}</small>
									<span>{{ row.right }}</span>
								</div>
							</div>
						</div>
					</section>

					<div class="mapper-basic-custom-callout">
						<div>
							<strong>Not finding what you need?</strong>
							<span>Create a custom mapping or duplicate an existing one in the advanced editor.</span>
						</div>
						<button type="button" class="action-btn" (click)="setMapperScreenMode('advanced')">Create custom template</button>
					</div>

					<div class="mapper-basic-about-card" *ngIf="selectedTemplate as template">
						<h4>About this template</h4>
						<p>{{ selectedTemplateActionSummary }}</p>
						<div class="mapper-basic-about-features">
							<div><strong>Pre-built mappings</strong><span>Based on common use cases.</span></div>
							<div><strong>Test before activate</strong><span>Verify with sample data.</span></div>
							<div><strong>Easy to customize</strong><span>Open Advanced when needed.</span></div>
						</div>
					</div>
				</main>

				<aside class="mapper-basic-preview-panel" data-cy="mapper-basic-selected-template">
					<div class="mapper-basic-preview-head" *ngIf="selectedTemplate as template; else noSelectedTemplate">
						<div>
							<h4>Test your mapping</h4>
							<p>{{ template.name }}. Load a sample and follow the data route into the target preview.</p>
						</div>
						<div class="mapper-basic-preview-head-actions">
							<span class="mapper-basic-field-pill">{{ selectedTemplateFieldCountLabel }}</span>
							<button type="button" class="action-btn action-btn-primary" data-cy="mapper-basic-apply-selected-template-button" (click)="applySelectedTemplateForBasicSetup()" [disabled]="!canApplySelectedTemplate">Continue to test</button>
						</div>
					</div>
					<ng-template #noSelectedTemplate>
						<div class="mapper-basic-empty">Select a template to see the preview.</div>
					</ng-template>

					<div class="mapper-basic-template-loading" *ngIf="isLoadingSelectedTemplateDefinition"><span class="spinner spinner-sm"></span><span>Loading preview...</span></div>
					<div class="mapper-basic-template-error" *ngIf="selectedTemplateDefinitionError">{{ selectedTemplateDefinitionError }}</div>

					<section class="mapper-basic-test-card">
						<div class="mapper-basic-sample-box" *ngIf="hasSampleInputs">
							<div class="sample-input-row compact" *ngFor="let input of sampleInputDefinitions">
								<label class="sample-input-label" [attr.for]="'basic-sample-input-' + input.key">{{ input.description }}</label>
								<input [attr.id]="'basic-sample-input-' + input.key" [attr.data-cy]="'mapper-basic-sample-input-' + input.key" class="grid-search sample-input-control" type="text" [value]="sampleInputValues[input.key] ?? ''" (input)="onSampleInputChanged(input.key, $any($event.target).value)" [placeholder]="input.placeholder || input.label" />
							</div>
							<button type="button" class="action-btn" data-cy="mapper-basic-load-sample-button" (click)="loadSampleSourcePayload()" [disabled]="!canLoadSamplePayload || isPageLoading">{{ loadSamplePayloadButtonLabel }}</button>
						</div>
						<div class="mapper-basic-result-ok" *ngIf="validationSummary.length && mappingStats.invalid === 0">All required fields mapped successfully.</div>
						<section class="mapper-basic-route-section">
							<div class="mapper-basic-value-head">
								<div>
									<strong>Preview data route</strong>
									<span>Follow each value from source sample through the mapper rule into the target preview.</span>
								</div>
							</div>
							<div class="mapper-basic-empty" *ngIf="!hasBasicPreviewDataRouteRows">{{ basicPreviewDataRouteEmptyText }}</div>
							<div class="mapper-basic-route-grid" *ngIf="hasBasicPreviewDataRouteRows">
								<div class="mapper-basic-route-grid-head">
									<span>Source sample</span>
									<span>Mapper rule</span>
									<span>Target preview</span>
								</div>
								<div class="mapper-basic-route-row" [attr.data-tone]="row.tone" [class.is-link-key]="row.isLinkKey" *ngFor="let row of basicPreviewDataRouteRows">
									<div class="mapper-basic-route-cell">
										<span>{{ row.sourceLabel }}</span>
										<strong>{{ row.sourceValue || 'Empty' }}</strong>
									</div>
									<div class="mapper-basic-route-rule">
										<span>{{ row.ruleLabel }}</span>
										<strong>{{ row.ruleDetail }}</strong>
									</div>
									<div class="mapper-basic-route-cell">
										<span>{{ row.targetLabel }}</span>
										<strong>{{ row.targetValue || 'Empty' }}</strong>
									</div>
								</div>
							</div>
						</section>
						<div class="mapper-basic-actions">
							<button type="button" class="action-btn action-btn-primary" data-cy="mapper-basic-run-test-button" (click)="runMapperTestLink()" [disabled]="!canRunTestLink" [attr.title]="testRunButtonTitle">{{ testRunButtonLabel }}</button>
						</div>
					</section>
				</aside>
			</section>

			<section class="mapping-shell workbench-card" *ngIf="showAdvancedMapperScreen">
				<div class="mapping-overview" *ngIf="hasSampleInputs || showLoadingBanner || selectedMappingNode?.kind === 'root'">
					<div class="mapping-inline-sample" *ngIf="hasSampleInputs" [attr.data-tone]="sampleInputStatusTone">
						<div class="mapping-inline-sample-head">
							<div class="mapping-inline-sample-copy">
								<div class="mapping-inline-sample-brand">
									<div>
										<div class="section-kicker">Source sample</div>
										<h3>Retrieve source sample</h3>
										<p class="mapping-inline-sample-helper">{{ sampleInputHelperText }}</p>
										<p class="mapping-inline-sample-status" *ngIf="showSampleLoadStatusText">
											{{ configuration?.samplePayloadStatusMessage }}
										</p>
									</div>
								</div>
								<div class="mapping-inline-sample-status-row">
									<span class="state-pill" [attr.data-tone]="sampleInputStatusTone === 'attention' ? 'warning' : sampleInputStatusTone === 'ready' ? 'success' : sampleInputStatusTone">
									{{ sampleInputStatusLabel }}
								</span>
								</div>
							</div>
						</div>

						<div class="sample-input-list compact">
							<div class="sample-input-row compact" *ngFor="let input of sampleInputDefinitions">
								<label class="sample-input-label" [attr.for]="'sample-input-' + input.key">
									<span class="sample-input-label-row">
										<span>{{ input.description }}</span>
									</span>

								</label>
								<input [attr.id]="'sample-input-' + input.key" [attr.data-cy]="'mapper-sample-input-' + input.key" class="grid-search sample-input-control" type="text" [value]="sampleInputValues[input.key] ?? ''" (input)="onSampleInputChanged(input.key, $any($event.target).value)" [placeholder]="input.placeholder || input.label"
								/>
							</div>
						</div>

						<div class="mapping-inline-sample-actions">
							<div class="sample-load-menu" [class.is-open]="sampleOptionsExpanded">
								<div class="sample-load-split">
									<button type="button" class="action-btn action-btn-primary mapping-inline-sample-load" data-cy="mapper-load-sample-button" (click)="loadSampleSourcePayload()" [disabled]="!canLoadSamplePayload || isPageLoading">
									{{ loadSamplePayloadButtonLabel }}
								</button>
									<button type="button" class="sample-options-toggle" [class.is-open]="sampleOptionsExpanded" (click)="sampleOptionsExpanded = !sampleOptionsExpanded" [attr.aria-expanded]="sampleOptionsExpanded" title="Sample options" aria-label="Toggle sample options">
									<i class="far" [ngClass]="sampleOptionsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
								</button>
								</div>

								<div class="sample-options-panel" *ngIf="sampleOptionsExpanded">
									<label class="sample-input-remember" title="Store these sample input values in this browser for this mapper.">
									<input type="checkbox" [checked]="rememberSampleInputValues" (change)="onRememberSampleInputValuesChanged($any($event.target).checked)" />
									<span>Remember values</span>
								</label>
								</div>
							</div>
							<div class="mapping-inline-sample-success" *ngIf="showSampleLoadSuccessIndicator" [attr.title]="configuration?.samplePayloadStatusMessage || null">
								<span class="mapping-inline-sample-success-icon" aria-hidden="true">✓</span>
								<span>{{ sampleLoadSuccessLabel }}</span>
							</div>
						</div>
					</div>

					<div class="mapping-inline-sample" *ngIf="hasTargetSchemaEnrichment" data-tone="muted">
						<div class="mapping-inline-sample-head">
							<div class="mapping-inline-sample-copy">
								<div class="mapping-inline-sample-brand">
									<div>
										<div class="section-kicker">Target enrichment</div>
										<h3>2. {{ targetSchemaEnrichmentConfig?.title || 'Target model enrichment' }}</h3>
										<p class="mapping-inline-sample-helper">{{ targetSchemaEnrichmentConfig?.description }}</p>
									</div>
								</div>
								<div class="mapping-inline-sample-status-row">
									<span class="state-pill" data-tone="muted">Optional</span>
								</div>
							</div>
						</div>

						<div class="sample-input-list compact">
							<div class="sample-input-row compact" *ngFor="let input of targetSchemaEnrichmentInputDefinitions">
								<label class="sample-input-label" [attr.for]="'target-enrichment-input-' + input.key">
								<span class="sample-input-label-row">
									<span>{{ input.label }}</span>
								</span>
								<span *ngIf="input.description">{{ input.description }}</span>
							</label>
								<input [attr.id]="'target-enrichment-input-' + input.key" class="grid-search sample-input-control" type="text" [value]="targetSchemaEnrichmentInputValues[input.key] ?? ''" (input)="onTargetSchemaEnrichmentInputChanged(input.key, $any($event.target).value)"
									[placeholder]="input.placeholder || ''" />
							</div>
						</div>

						<div class="mapping-inline-sample-actions">
							<button type="button" class="action-btn action-btn-secondary mapping-inline-sample-load" (click)="refreshTargetSchemaEnrichment()" [disabled]="!canRefreshTargetSchemaEnrichment">
							{{ isTargetSchemaEnrichmentLoading ? 'Refreshing...' : (targetSchemaEnrichmentConfig?.refreshButtonLabel || 'Refresh target model') }}
						</button>
						</div>
					</div>

					<div class="loading-banner" *ngIf="showLoadingBanner">
						<span class="spinner spinner-sm"></span>
						<span class="loading-banner-copy">
							<strong>{{ pageLoadingMessage }}</strong>
							<small>{{ pageLoadingDetail }}</small>
						</span>
					</div>
				</div>

				<div class="mapper-node-shell" *ngIf="showMappingObjectTabs">
					<div class="mapper-object-strip">
						<div class="section-kicker">Objects</div>
						<div class="mapper-object-tabs" role="tablist" aria-label="Mapped objects">
							<button type="button" class="mapper-object-tab" *ngFor="let item of mappingObjectTabs" [class.is-selected]="selectedMappingNode?.id === item.nodeId" [attr.aria-selected]="selectedMappingNode?.id === item.nodeId" (pointerdown)="onObjectTabPointerDown($event, item.nodeId)"
								(click)="selectMappingNode(item.nodeId)">
								<span>{{ item.label }}</span>
							</button>
						</div>
					</div>

					<div class="mapper-node-config-card" *ngIf="!selectedNodeIsRoot && selectedMappingNode as selectedNode">
						<div class="mapper-node-config-head">
							<div class="mapper-node-config-copy">
								<strong>Child mapping settings</strong>
								<span>{{ selectedNodeTitle }} · {{ selectedNodeSubtitle }}</span>
							</div>
							<button type="button" class="action-btn action-btn-ghost" (click)="selectRootMappingNode()" [disabled]="isPageLoading">Back to Accounts</button>
							<button type="button" class="action-btn action-btn-ghost" (click)="removeSelectedMappingNode()" [disabled]="isPageLoading">Remove child</button>
						</div>

						<div class="mapper-node-config-grid">
							<label class="mapper-node-config-field">
							<span>Name</span>
							<input class="grid-search" type="text" [ngModel]="selectedNode.name" (ngModelChange)="selectedNode.name = $event || ''" placeholder="Contacts sync" />
						</label>

							<label class="mapper-node-config-field" *ngIf="!selectedNode.inlineChildObject">
							<span>Source entity</span>
							<select class="grid-search writeback-select" [ngModel]="selectedNode.sourceEntity" (ngModelChange)="onSelectedNodeSourceEntityChanged($event)">
								<option *ngFor="let entity of childSourceEntityOptions" [ngValue]="entity.entityName">{{ entity.displayName || entity.entityName }}</option>
							</select>
						</label>

							<label class="mapper-node-config-field" *ngIf="!selectedNode.inlineChildObject">
							<span>Target entity</span>
							<select class="grid-search writeback-select" [ngModel]="selectedNode.targetEntity" (ngModelChange)="onSelectedNodeTargetEntityChanged($event)">
								<option *ngFor="let entity of childTargetEntityOptions" [ngValue]="entity.entityName">{{ entity.displayName || entity.entityName }}</option>
							</select>
						</label>

							<label class="mapper-node-config-field" *ngIf="!selectedNode.inlineChildObject">
							<span>Source collection path</span>
							<input class="grid-search" type="text" [ngModel]="selectedNode.sourceCollectionPath" (ngModelChange)="selectedNode.sourceCollectionPath = $event || ''" placeholder="AccountingContacts" />
							<small>Leave blank to map a single child from the parent source record, for example an account IBAN UDF into one bank account child.</small>
						</label>

							<label class="mapper-node-config-field" *ngIf="!selectedNode.inlineChildObject">
							<span>Target runtime route</span>
							<select class="grid-search writeback-select" *ngIf="selectedNodeTargetRuntimeRouteOptions.length; else customTargetRuntimeRouteInput" [ngModel]="selectedNode.targetRuntimeRoute" (ngModelChange)="selectedNode.targetRuntimeRoute = $event || ''">
								<option *ngFor="let route of selectedNodeTargetRuntimeRouteOptions" [ngValue]="route">{{ route }}</option>
							</select>
							<ng-template #customTargetRuntimeRouteInput>
								<input class="grid-search" type="text" [ngModel]="selectedNode.targetRuntimeRoute" (ngModelChange)="selectedNode.targetRuntimeRoute = $event || ''" placeholder="link-mapper/runtime/contacts/upsert" />
							</ng-template>
						</label>

							<label class="mapper-node-config-field" *ngIf="!selectedNode.inlineChildObject">
							<span>Continue on error</span>
							<select class="grid-search writeback-select" [ngModel]="selectedNode.continueOnError" (ngModelChange)="selectedNode.continueOnError = $event === true || $event === 'true'">
								<option [ngValue]="true">Yes</option>
								<option [ngValue]="false">No</option>
							</select>
						</label>

							<label class="mapper-node-config-field" *ngIf="!selectedNode.inlineChildObject">
							<span>Execution</span>
							<select class="grid-search writeback-select" [ngModel]="selectedNode.executionMode" (ngModelChange)="selectedNode.executionMode = $event || 'AfterParentUpsert'">
								<option *ngFor="let option of childExecutionModeOptions" [ngValue]="option.value">{{ option.label }}</option>
							</select>
							<small>Before parent upsert cannot use parent target-result bindings because the target record does not exist yet.</small>
						</label>

							<div class="mapper-node-config-field" *ngIf="selectedNode.inlineChildObject">
								<span>Inline child object</span>
								<small>This mapper tab writes back into the parent account payload at <code>{{ selectedNode.ownerTargetPath }}</code>.</small>
							</div>
						</div>

						<div class="mapper-node-bindings">
							<div class="mapper-node-bindings-head">
								<strong>Context bindings</strong>
								<button type="button" class="action-btn action-btn-ghost" (click)="addContextBinding()">Add binding</button>
							</div>

							<div class="mapper-node-bindings-empty" *ngIf="!selectedNode.contextBindings.length">
								No context bindings configured yet.
							</div>

							<div class="mapper-node-binding-row" *ngFor="let binding of selectedNode.contextBindings; let i = index">
								<input class="grid-search" type="text" [ngModel]="binding.targetPath" (ngModelChange)="binding.targetPath = $event || ''" placeholder="ParentId" />
								<select class="grid-search writeback-select" [ngModel]="binding.mode" (ngModelChange)="binding.mode = $event">
									<option *ngFor="let option of childContextBindingModeOptions" [ngValue]="option.value">{{ option.label }}</option>
								</select>
								<input class="grid-search" type="text" [ngModel]="binding.sourcePath" (ngModelChange)="binding.sourcePath = $event || ''" placeholder="ID or Code" />
								<button type="button" class="action-btn action-btn-ghost" (click)="removeContextBinding(i)">Remove</button>
							</div>
						</div>
					</div>

				</div>

				<div class="mapping-stage-shell" [class.is-locked]="activeMapperTab !== 'writeback' && activeMapperTab !== 'messages' && isGridLocked">
					<div class="mapper-tab-shell">
						<div class="mapper-tabs">
							<button type="button" class="mapper-tab-btn" data-cy="mapper-tab-create" [class.active]="activeMapperTab === 'create'" (click)="setActiveMapperTab('create')">Create</button>
							<button type="button" class="mapper-tab-btn optional" data-cy="mapper-tab-update" [class.active]="activeMapperTab === 'update'" [disabled]="!canShowUpdateTab" (click)="setActiveMapperTab('update')">
								<span>Update</span>
								<small>Optional</small>
							</button>
							<button type="button" class="mapper-tab-btn" data-cy="mapper-tab-writeback" [class.active]="activeMapperTab === 'writeback'" (click)="setActiveMapperTab('writeback')">Writeback</button>
							<button type="button" class="mapper-tab-btn" data-cy="mapper-tab-messages" [class.active]="activeMapperTab === 'messages'" (click)="setActiveMapperTab('messages')">Messages</button>
						</div>

						<div class="mapper-tab-summary-row">
							<div class="mapper-tab-copy">
								<strong>{{ currentTabTitle }}</strong>
								<span>{{ currentTabDescription }}</span>
							</div>

							<div class="mapper-tab-actions" *ngIf="activeMapperTab === 'create' || activeMapperTab === 'update' || activeMapperTab === 'writeback'">
								<button type="button" class="action-btn action-btn-ghost" data-cy="mapper-copy-from-other-tab-button" *ngIf="activeMapperTab === 'create' || activeMapperTab === 'update'" (click)="copyMappingsFromOtherTemplate()" [disabled]="isPageLoading">
									{{ copyFromTemplateLabel }}
								</button>
								<button type="button" class="action-btn" data-cy="mapper-add-writeback-button" *ngIf="activeMapperTab === 'writeback'" (click)="addWriteback()" [disabled]="!sourceWritableFieldOptions.length">Add {{ activeWritebackRuleSet }} writeback</button>
							</div>
						</div>
					</div>

					<div class="mapping-shell-controls filter-toolbar" *ngIf="activeMapperTab !== 'writeback' && activeMapperTab !== 'messages' && !isGridLocked">
								<div class="mapping-shell-search">
									<input class="grid-search" data-cy="mapper-row-search" type="text" [value]="mappingRowSearch" (input)="onMappingRowSearchChanged($any($event.target).value)" placeholder="Search fields..." />
								</div>

								<div class="focus-filters">
									<button type="button" class="filter-pill" data-cy="mapper-filter-all" [class.active]="activeToolbarFilter === 'all'" (click)="setToolbarFilter('all')">
								<span>All</span>
								<strong>{{ mappingStats.total }}</strong>
							</button>
									<button type="button" class="filter-pill" data-cy="mapper-filter-required" [class.active]="activeToolbarFilter === 'required'" (click)="setToolbarFilter('required')">
								<span>Required</span>
								<strong>{{ mappingStats.mappedRequired }}/{{ mappingStats.required }}</strong>
							</button>
									<button type="button" class="filter-pill" data-cy="mapper-filter-mapped" [class.active]="activeToolbarFilter === 'mapped'" (click)="setToolbarFilter('mapped')">
								<span>Mapped</span>
								<strong>{{ mappingStats.mapped }}</strong>
							</button>
									<button type="button" class="filter-pill" data-cy="mapper-filter-unmapped" [class.active]="activeToolbarFilter === 'unmapped'" (click)="setToolbarFilter('unmapped')">
								<span>Unfinished</span>
								<strong>{{ mappingStats.unmappedRequired }}</strong>
							</button>
									<button type="button" class="filter-pill" data-cy="mapper-filter-issues" [class.active]="activeToolbarFilter === 'issues'" (click)="setToolbarFilter('issues')">
								<span>Issues</span>
								<strong>{{ mappingStats.invalid }}</strong>
							</button>
									<button type="button" class="filter-pill" data-cy="mapper-filter-ignored" [class.active]="activeToolbarFilter === 'ignored'" (click)="setToolbarFilter('ignored')">
								<span>Ignored</span>
								<strong>{{ mappingStats.ignored }}</strong>
							</button>
									<button type="button" class="filter-pill" data-cy="mapper-filter-hide-readonly" [class.active]="hideReadOnlyRows" (click)="toggleReadOnlyRows()">
								<span>Hide read-only</span>
							</button>
								</div>

								<button type="button" class="action-btn action-btn-ghost" data-cy="mapper-reset-filters-button" (click)="resetFilters()" [disabled]="!hasActiveFilters">Reset</button>
					</div>

					<div class="mapping-content-shell" *ngIf="activeMapperTab === 'writeback' || activeMapperTab === 'messages' || !isGridLocked">
						<div class="writeback-tab-panel" data-cy="mapper-writeback-panel" *ngIf="activeMapperTab === 'writeback'; else tabContentPanel" (mapper-configure-writeback)="configureWritebackFromEvent($event)">
							<div class="writeback-rule-set-switch" role="tablist" aria-label="Writeback operation">
								<button type="button" role="tab" [class.active]="activeWritebackRuleSet === 'create'" [attr.aria-selected]="activeWritebackRuleSet === 'create'" (click)="setActiveWritebackRuleSet('create')">
									<span>After create</span>
									<strong>{{ createWritebacks.length }}</strong>
								</button>
								<button type="button" role="tab" [class.active]="activeWritebackRuleSet === 'update'" [attr.aria-selected]="activeWritebackRuleSet === 'update'" (click)="setActiveWritebackRuleSet('update')">
									<span>After update</span>
									<strong>{{ updateWritebacks.length }}</strong>
								</button>
							</div>
							<div class="mapper-node-config-card" *ngIf="selectedNodeIsRoot && selectedMappingNode as selectedNode">
								<div class="mapper-node-config-grid">
									<label class="mapper-node-config-field">
									<span>Source ID path</span>
									<div class="source-dropdown-editor writeback-picker" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()" (dblclick)="$event.stopPropagation()">
									<dx-drop-down-box data-cy="mapper-source-id-path-select" [value]="selectedNode.sourceIdPath" [dataSource]="selectedNode.sourceFieldOptions" valueExpr="path" displayExpr="label" [opened]="sourceIdPathDropdownOpened" [deferRendering]="true"
											placeholder="Select source ID field" [dropDownOptions]="mapperTreeDropDownOptions" (onOpened)="sourceIdPathDropdownOpened = true" (onClosed)="sourceIdPathDropdownOpened = false">
											<div *dxTemplate="let data of 'content'">
											<div class="source-dropdown-content mapper-tree-dropdown-content" data-cy="mapper-source-id-path-tree" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()">
													<div class="source-tree-search">
														<input class="grid-search" type="text" [value]="sourceIdPathFieldSearch" (input)="setSourceIdPathFieldSearchValue($any($event.target).value)" placeholder="Search {{ sourceSystemName }} fields..." />
													</div>

													<div class="selected-source-field" *ngIf="selectedNode.sourceIdPath">
														Selected: <strong>{{ selectedNode.sourceIdPath }}</strong>
													</div>

												<div class="source-tree-body" (wheel)="$event.stopPropagation()">
													<dx-tree-view [items]="getCachedSourceIdPathTreeItems()" dataStructure="tree" keyExpr="id" displayExpr="displayName" itemsExpr="items" [searchEnabled]="false" [expandEvent]="'click'" [selectByClick]="true" [focusStateEnabled]="false" [activeStateEnabled]="true"
														(onItemClick)="onSourceIdPathTreeItemClick($event)">
														<div *dxTemplate="let item of 'item'" class="mapper-tree-field-option">
															<strong>{{ item.displayName || item.path }}</strong>
															<small *ngIf="item.path && item.path !== item.displayName">{{ item.path }}</small>
														</div>
													</dx-tree-view>
													</div>
												</div>
											</div>
										</dx-drop-down-box>
									</div>
									<small>Field path in the source payload used to identify the record for writeback.</small>
								</label>
								</div>
							</div>

							<div class="writeback-grid-shell" *ngIf="writebacks.length; else writebackEmptyState">
								<div class="writeback-grid-table">
									<div class="writeback-grid-row writeback-grid-header">
										<div class="writeback-grid-cell">Field ({{ sourceSystemName }})</div>
										<div class="writeback-grid-cell">Value type</div>
										<div class="writeback-grid-cell">Value</div>
										<div class="writeback-grid-cell">Condition</div>
										<div class="writeback-grid-cell">Actions</div>
									</div>

									<div class="writeback-grid-row writeback-data-row" [attr.data-cy]="'mapper-writeback-row-' + i" *ngFor="let writeback of writebacks; let i = index">
										<div class="writeback-grid-cell">
											<label class="writeback-field">
											<span class="writeback-cell-label">Update this {{ sourceSystemName }} field</span>
											<div class="source-dropdown-editor writeback-picker" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()" (dblclick)="$event.stopPropagation()">
												<dx-drop-down-box [attr.data-cy]="'mapper-writeback-source-field-select-' + i" [value]="writeback.sourceFieldPath" [dataSource]="sourceWritableFieldOptions" valueExpr="path" displayExpr="label" [opened]="writebackSourceDropdownOpenedByIndex[i]" [deferRendering]="true"
													placeholder="Select {{ sourceSystemName }} field" [dropDownOptions]="mapperTreeDropDownOptions" (onOpened)="writebackSourceDropdownOpenedByIndex[i] = true" (onClosed)="writebackSourceDropdownOpenedByIndex[i] = false">
													<div *dxTemplate="let data of 'content'">
														<div class="source-dropdown-content mapper-tree-dropdown-content" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()">
															<div class="source-tree-search">
																<input class="grid-search" type="text" [value]="getWritebackSourceFieldSearchValue(i)" (input)="setWritebackSourceFieldSearchValue(i, $any($event.target).value)" placeholder="Search {{ sourceSystemName }} fields..." />
															</div>

															<div class="selected-source-field" *ngIf="writeback.sourceFieldPath">
																Selected: <strong>{{ writeback.sourceFieldPath }}</strong>
															</div>

															<div class="source-tree-body" (wheel)="$event.stopPropagation()">
																<dx-tree-view [items]="getCachedWritebackSourceTreeItems(i)" dataStructure="tree" keyExpr="id" displayExpr="displayName" itemsExpr="items" [searchEnabled]="false" [expandEvent]="'click'" [selectByClick]="true" [focusStateEnabled]="false" [activeStateEnabled]="true"
																	(onItemClick)="onWritebackSourceTreeItemClick($event, writeback, i)">
																	<div *dxTemplate="let item of 'item'" class="mapper-tree-field-option">
																		<strong>{{ item.displayName || item.path }}</strong>
																		<small *ngIf="item.path && item.path !== item.displayName">{{ item.path }}</small>
																	</div>
																</dx-tree-view>
															</div>
														</div>
													</div>
												</dx-drop-down-box>
											</div>
										</label>
										</div>

										<div class="writeback-grid-cell">
											<label class="writeback-field">
											<span class="writeback-cell-label">Write this value back</span>
											<dx-select-box [items]="writebackModeOptions" displayExpr="label" valueExpr="value" [value]="writeback.mode" width="100%" (onValueChanged)="onWritebackModeChanged(writeback, $event.value)">
											</dx-select-box>
										</label>
										</div>

									<div class="writeback-grid-cell">
										<label class="writeback-field" *ngIf="writeback.mode === 'TargetValue'">
										<span class="writeback-cell-label">Returned {{ targetSystemName }} field</span>
										<div class="source-dropdown-editor writeback-picker" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()" (dblclick)="$event.stopPropagation()">
											<dx-drop-down-box [attr.data-cy]="'mapper-writeback-target-field-select-' + i" [value]="writeback.targetFieldPath" [dataSource]="targetWritebackFieldOptions" valueExpr="path" [displayExpr]="writebackTargetFieldDisplayExpr" [opened]="writebackTargetDropdownOpenedByIndex[i]" [deferRendering]="true" [showClearButton]="true"
												[dropDownOptions]="mapperTreeDropDownOptions" placeholder="Select {{ targetSystemName }} field" (onOpened)="writebackTargetDropdownOpenedByIndex[i] = true" (onClosed)="writebackTargetDropdownOpenedByIndex[i] = false" (onValueChanged)="onWritebackTargetFieldChanged(writeback, $event.value)">
												<div *dxTemplate="let data of 'content'">
													<div class="source-dropdown-content mapper-tree-dropdown-content" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()">
														<div class="source-tree-search">
															<input class="grid-search" type="text" [value]="getWritebackTargetFieldSearchValue(i)" (input)="setWritebackTargetFieldSearchValue(i, $any($event.target).value)" placeholder="Search {{ targetSystemName }} fields..." />
														</div>

														<div class="selected-source-field" *ngIf="writeback.targetFieldPath">
															Selected: <strong>{{ writeback.targetFieldPath }}</strong>
														</div>

														<div class="source-tree-body" (wheel)="$event.stopPropagation()">
															<dx-tree-view [items]="getCachedWritebackTargetTreeItems(i)" dataStructure="tree" keyExpr="id" displayExpr="displayName" itemsExpr="items" [searchEnabled]="false" [expandEvent]="'click'" [selectByClick]="true" [focusStateEnabled]="false" [activeStateEnabled]="true"
																(onItemClick)="onWritebackTargetTreeItemClick($event, writeback, i)">
																<div *dxTemplate="let item of 'item'" class="mapper-tree-field-option">
																	<strong>{{ item.displayName || item.path }}</strong>
																	<small *ngIf="item.path && item.path !== item.displayName">{{ item.path }}</small>
																</div>
															</dx-tree-view>
														</div>
													</div>
												</div>
											</dx-drop-down-box>
										</div>
									</label>

											<label class="writeback-field" *ngIf="writeback.mode === 'CurrentDate'">
											<span class="writeback-cell-label">Date format to write into {{ sourceSystemName }}</span>
											<dx-select-box [items]="writebackDateFormatOptions" [value]="writeback.dateFormat" width="100%" (valueChange)="onWritebackDateFormatChanged(writeback, $event)">
											</dx-select-box>
										</label>

											<label class="writeback-field" *ngIf="writeback.mode === 'Expression'">
											<span class="writeback-cell-label">Expression value</span>
											<textarea class="grid-search runtime-message-textarea" [ngModel]="writeback.expression ?? ''" (ngModelChange)="onWritebackExpressionChanged(writeback, $event)" placeholder="Example: Iif(Result.partyTypeEnum == 'ORGANIZATION', Result.customerNumber, Result.supplierNumber)" rows="2"></textarea>
											<button type="button" class="action-btn" (click)="openWritebackExpressionEditor(i, 'value')">Edit expression</button>
										</label>
										</div>

										<div class="writeback-grid-cell">
											<label class="writeback-field">
											<span class="writeback-cell-label">Only write back when</span>
											<textarea class="grid-search runtime-message-textarea" [attr.data-cy]="'mapper-writeback-condition-' + i" [ngModel]="writeback.conditionExpression ?? ''" (ngModelChange)="onWritebackConditionChanged(writeback, $event)" placeholder="Optional. Example: [AccountNumber] == null || [AccountNumber] == ''" rows="2"></textarea>
											<button type="button" class="action-btn" (click)="openWritebackExpressionEditor(i, 'condition')">Edit condition</button>
										</label>
										</div>

										<div class="writeback-grid-cell writeback-actions-cell">
											<button type="button" class="action-btn action-btn-ghost" (click)="removeWriteback(i)">Remove</button>
										</div>
									</div>
								</div>
							</div>

							<ng-template #writebackEmptyState>
								<div class="writeback-grid-shell writeback-empty-shell" data-cy="mapper-writeback-empty-state">
									<div class="writeback-empty">No writebacks configured for {{ activeWritebackRuleSet === 'create' ? 'created' : 'updated' }} records.</div>
								</div>
							</ng-template>
						</div>

						<ng-template #tabContentPanel>
							<div class="runtime-message-card" data-cy="mapper-messages-panel" *ngIf="activeMapperTab === 'messages'; else mappingGridPanel" (mapper-set-runtime-message-template)="setRuntimeMessageTemplateFromEvent($event)">
								<div class="runtime-message-grid-shell">
									<dx-data-grid [dataSource]="runtimeMessageTemplateExamples" keyExpr="key" [showBorders]="false" [columnAutoWidth]="false" [rowAlternationEnabled]="false" [hoverStateEnabled]="true" [wordWrapEnabled]="true" [width]="'100%'" [paging]="{ enabled: false }"
										[sorting]="{ mode: 'none' }" [focusedRowEnabled]="false">
										<dxo-scrolling mode="standard" [useNative]="true" [scrollByContent]="true" [scrollByThumb]="true" showScrollbar="onHover"></dxo-scrolling>
										<dxo-keyboard-navigation [enabled]="false"></dxo-keyboard-navigation>
										<dxo-search-panel [visible]="false"></dxo-search-panel>
										<dxo-column-chooser [enabled]="false"></dxo-column-chooser>
										<dxo-export [enabled]="false"></dxo-export>

										<dxi-column dataField="label" caption="Message" [width]="260" cellTemplate="runtimeMessageNameTemplate"></dxi-column>
										<dxi-column caption="Template" cellTemplate="runtimeMessageTemplateEditor"></dxi-column>

										<div *dxTemplate="let cell of 'runtimeMessageNameTemplate'">
											<div class="runtime-message-name-cell">
												<strong>{{ cell.data.label }}</strong>
												<span>{{ cell.data.description }}</span>
											</div>
										</div>

										<div *dxTemplate="let cell of 'runtimeMessageTemplateEditor'">
										<dx-text-area class="runtime-message-template-editor" [attr.data-cy]="'mapper-runtime-message-template-' + cell.data.key" [value]="runtimeMessageTemplates[cell.data.key] ?? ''" (valueChange)="onRuntimeMessageTemplateChanged(cell.data.key, $event)" [placeholder]="cell.data.placeholder" [height]="78">
											</dx-text-area>
										</div>
									</dx-data-grid>
								</div>

								<div class="runtime-message-variable-shell">
									<div class="runtime-message-variable-card">
										<div class="runtime-message-variable-head">
											<strong>Source properties</strong>
											<span>Browse available {{ sourceSystemName }} fields and copy a token.</span>
										</div>

									<div class="source-dropdown-editor writeback-picker" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()" (dblclick)="$event.stopPropagation()">
									<dx-drop-down-box [value]="runtimeMessageSourceVariableDisplayValue" [dataSource]="runtimeMessageSourcePropertyExamples" [opened]="messageVariableSourceDropdownOpened" [deferRendering]="true" placeholder="Select source property" [dropDownOptions]="mapperTreeDropDownOptions"
											(onOpened)="messageVariableSourceDropdownOpened = true" (onClosed)="messageVariableSourceDropdownOpened = false">
											<div *dxTemplate="let data of 'content'">
												<div class="source-dropdown-content mapper-tree-dropdown-content" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()">
														<div class="source-tree-search">
															<input class="grid-search" type="text" [value]="messageVariableSourceSearch" (input)="messageVariableSourceSearch = $any($event.target).value" placeholder="Search {{ sourceSystemName }} fields..." />
														</div>

														<div class="selected-source-field">Click a field to copy <strong>{{ '{{source.path}}' }}</strong></div>

														<div class="source-tree-body" (wheel)="$event.stopPropagation()">
															<dx-tree-view [items]="filteredRuntimeMessageSourceTreeItems" dataStructure="tree" keyExpr="id" displayExpr="displayName" itemsExpr="items" [searchEnabled]="false" [expandEvent]="'click'" [selectByClick]="true" [focusStateEnabled]="false" [activeStateEnabled]="true"
																(onItemClick)="onRuntimeMessageSourceItemClick($event)">
															</dx-tree-view>
														</div>
													</div>
												</div>
											</dx-drop-down-box>
										</div>
									</div>

									<div class="runtime-message-variable-card">
										<div class="runtime-message-variable-head">
											<strong>Target properties</strong>
											<span>Browse available {{ targetSystemName }} fields and copy a token.</span>
										</div>

									<div class="source-dropdown-editor writeback-picker" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()" (dblclick)="$event.stopPropagation()">
									<dx-drop-down-box [value]="runtimeMessageTargetVariableDisplayValue" [dataSource]="runtimeMessageTargetPropertyExamples" [opened]="messageVariableTargetDropdownOpened" [deferRendering]="true" placeholder="Select target property" [dropDownOptions]="mapperTreeDropDownOptions"
											(onOpened)="messageVariableTargetDropdownOpened = true" (onClosed)="messageVariableTargetDropdownOpened = false">
											<div *dxTemplate="let data of 'content'">
												<div class="source-dropdown-content mapper-tree-dropdown-content" (click)="$event.stopPropagation()" (mousedown)="$event.stopPropagation()">
														<div class="source-tree-search">
															<input class="grid-search" type="text" [value]="messageVariableTargetSearch" (input)="messageVariableTargetSearch = $any($event.target).value" placeholder="Search {{ targetSystemName }} fields..." />
														</div>

														<div class="selected-source-field">Click a field to copy <strong>{{ '{{target.path}}' }}</strong></div>

														<div class="source-tree-body" (wheel)="$event.stopPropagation()">
															<dx-tree-view [items]="filteredRuntimeMessageTargetTreeItems" dataStructure="tree" keyExpr="id" displayExpr="displayName" itemsExpr="items" [searchEnabled]="false" [expandEvent]="'click'" [selectByClick]="true" [focusStateEnabled]="false" [activeStateEnabled]="true"
																(onItemClick)="onRuntimeMessageTargetItemClick($event)">
															</dx-tree-view>
														</div>
													</div>
												</div>
											</dx-drop-down-box>
										</div>
									</div>

									<div class="runtime-message-token-group">
										<strong>Runtime fields</strong>
										<div class="runtime-message-token-list">
											<button type="button" class="runtime-message-token runtime-message-token-btn" *ngFor="let token of runtimeMessageMetaTokenExamples" (click)="copyRuntimeMessageToken(token)">{{ token }}</button>
										</div>
									</div>
								</div>
							</div>

							<ng-template #mappingGridPanel>
								<app-mapping-rules-grid [schema]="selectedMappingNode?.targetSchema || config.targetSchema" [mappingRows]="filteredMappingRows" [sourceFieldOptions]="sourceFieldOptions" [sourceTreeItems]="sourceTreeItems" [referenceFieldOptionsByEntity]="referenceFieldOptionsByEntity"
									[selectedRowId]="selectedRow?.targetFieldId ?? null" [layoutStorageKey]="mappingGridLayoutStorageKey" [sourceSystemName]="sourceSystemName" [targetSystemName]="targetSystemName" [getRowState]="resolveMappingRowState" [getModeDescription]="resolveMappingModeDescription"
									(mappingModeChanged)="onMappingModeChanged($event)" (previewRequested)="requestPreview($event)" (expressionEditRequested)="openExpressionEditor($event)" (lookupEditRequested)="openLookupEditor($event)" (picklistMapRequested)="openPicklistMappingEditor($event)"
									(childMappingRequested)="openChildMappingFromRow($event)" (deferredSourceNodeExpanded)="onDeferredSourceNodeExpanded($event)" (linkKeyChanged)="onLinkKeyChanged($event)" (rowSelected)="onRowSelected($event)">
								</app-mapping-rules-grid>
							</ng-template>
						</ng-template>
					</div>
					<div class="grid-lock-shell" *ngIf="activeMapperTab !== 'writeback' && activeMapperTab !== 'messages' && isGridLocked">
						<div class="grid-lock-card">
							<div class="section-kicker">Setup required</div>
							<h3>{{ gridLockTitle }}</h3>
							<p>{{ gridLockMessage }}</p>
						</div>
					</div>
				</div>
			</section>
		</div>

		<aside class="inspector-column" *ngIf="showAdvancedMapperScreen">
			<section class="workbench-card inspector-card" data-cy="mapper-debug-panel">
				<app-mapping-debug-panel [previewJson]="previewResponseJson" [sourceJson]="previewSourceJson" [resultJson]="previewResultJson">
				</app-mapping-debug-panel>
			</section>
		</aside>
	</section>

	<app-expression-editor-popup [(visible)]="expressionPopupVisible" [draft]="expressionDraft" (draftChange)="onExpressionDraftChanged($event)" [fieldSearch]="expressionFieldSearch" (fieldSearchChange)="setExpressionFieldSearch($event)" [targetFieldSearch]="expressionTargetFieldSearch"
		(targetFieldSearchChange)="setExpressionTargetFieldSearch($event)" [(tipsExpanded)]="expressionTipsExpanded" [(expressionToolSearch)]="expressionToolSearch" [(selectedExpressionToolCategory)]="selectedExpressionToolCategory" [expressionToolCategories]="expressionToolCategories"
		[groupedExpressionTools]="groupedExpressionTools" [sourceTreeItems]="expressionEditorUsesGuideFields ? mapperGuideSourceTreeItems : expressionSourceTreeItems" [targetTreeItems]="expressionEditorUsesGuideFields ? mapperGuideTargetTreeItems : expressionTargetTreeItems" [sourceSystemName]="expressionEditorUsesGuideFields ? mapperGuideSourceSystemName : sourceSystemName" [targetSystemName]="expressionEditorUsesGuideFields ? mapperGuideTargetSystemName : targetSystemName" [showTargetFields]="showExpressionTargetFields"
		[selectedSourceField]="selectedExpressionSourceField" [editingRow]="expressionEditingRow" [expressionSuggestionsVisible]="expressionSuggestionsVisible" [expressionSuggestions]="expressionSuggestions" [expressionSuggestionIndex]="expressionSuggestionIndex"
		[canTestExpression]="canTestExpressionDraft" [expressionTestLoading]="expressionTestLoading" [expressionTestRan]="expressionTestRan" [expressionTestIsValid]="expressionTestIsValid" [expressionTestHasResult]="expressionTestHasResult" [expressionTestResult]="expressionTestResult"
		[expressionTestMessage]="expressionTestMessage" [expressionTestUsedValues]="expressionTestUsedValues" (cancel)="closeExpressionEditor()" (apply)="applyExpression()" (editorInitialized)="onExpressionEditorInitialized($event)" (editorInput)="onExpressionInput()"
		(editorFocusIn)="onExpressionClick()" (editorKeyDown)="onExpressionDxKeyDown($event)" (applySuggestion)="applyExpressionSuggestion($event)" (treeFieldSelected)="onExpressionTreeFieldSelected($event)" (targetTreeFieldSelected)="onExpressionTargetTreeFieldSelected($event)"
		(picklistFieldSelected)="onExpressionPicklistFieldSelected($event)" (insertTool)="insertExpressionTool($event)" (insertPicklistValue)="insertExpressionPicklistValue($event)" (insertPicklistComparison)="insertExpressionPicklistComparison($event)" (insertExample)="insertExpressionExample($event)"
		(testExpression)="testExpressionDraft()" (deferredSourceNodeExpanded)="onDeferredSourceNodeExpanded($event)">
	</app-expression-editor-popup>

	<app-lookup-editor-popup [(visible)]="lookupPopupVisible" [row]="lookupEditingDraftRow" [sourceFieldOptions]="guideLookupEditorActive ? mapperGuideSourceFieldOptions : sourceFieldOptions" [sourceTreeItems]="guideLookupEditorActive ? mapperGuideSourceTreeItems : sourceTreeItems" [referenceFieldOptions]="guideLookupEditorActive ? getMapperGuideLookupReferenceFieldOptions(lookupEditingDraftRow) : getLookupReferenceFieldOptions(lookupEditingDraftRow)"
		[sourceSystemName]="guideLookupEditorActive ? mapperGuideSourceSystemName : sourceSystemName"
		[sampleSourcePayload]="configuration?.sampleSourcePayload" [canTestLookup]="!guideLookupEditorActive && !!configuration?.samplePayloadRetrieved" [testLoading]="lookupTestLoading" [testRan]="lookupTestRan" [testValid]="lookupTestValid" [testHasResult]="lookupTestHasResult" [testResult]="lookupTestResult" [testMessage]="lookupTestMessage"
		(cancel)="closeLookupEditor()" (apply)="applyLookupEditor($event)" (testLookup)="testLookupEditor($event)" (draftChanged)="resetLookupTestState()" (criterionExpressionEditRequested)="openLookupCriterionExpressionEditor($event)" (deferredSourceNodeExpanded)="onDeferredSourceNodeExpanded($event)">
	</app-lookup-editor-popup>

		<app-picklist-value-mapping-popup [(visible)]="picklistMappingPopupVisible" [row]="picklistMappingRow" [sourceField]="picklistMappingSourceField" [sourceSystemName]="guidePicklistMappingActive ? mapperGuideSourceSystemName : config.sourceSchema.systemName" [targetSystemName]="guidePicklistMappingActive ? mapperGuideTargetSystemName : config.targetSchema.systemName" [sourceOptions]="picklistMappingSourceField?.picklistOptions || []"
		[targetOptions]="picklistMappingRow?.targetPicklistOptions || []" [mapping]="picklistMappingDraft" (cancel)="closePicklistMappingEditor()" (apply)="applyPicklistMapping($event)">
	</app-picklist-value-mapping-popup>

	<div class="mapper-guide-overlay" *ngIf="mapperDocumentationOpen" (click)="mapperDocumentationOpen = false">
		<section class="mapper-guide-panel mapper-guide-panel-wide" (click)="$event.stopPropagation()" role="dialog" aria-modal="true" aria-label="Generic mapper grid guide">
			<div class="mapper-guide-head">
				<div>
					<div class="section-kicker">Grid guide</div>
					<h2>How to use the mapping grid</h2>
					<p>Use this as a visual cheat sheet: pick a target field, choose a mode, fill the value, preview, then save.</p>
				</div>
				<button type="button" class="mapper-guide-close" (click)="mapperDocumentationOpen = false" aria-label="Close mapper guide">Close</button>
			</div>

			<div class="mapper-guide-simple">
				<nav class="mapper-guide-feature-list" aria-label="Grid features">
					<section class="mapper-guide-feature-group" *ngFor="let section of mapperGuideSections">
						<div class="mapper-guide-feature-group-head">
							<strong>{{ section.title }}</strong>
							<span>{{ section.description }}</span>
						</div>
						<button type="button" *ngFor="let row of section.rows" [class.is-selected]="row.targetFieldId === selectedMapperGuideRowId" (click)="selectMapperGuideRow(row)">
							<strong>{{ mapperGuideCaseDetails[row.targetFieldId].title }}</strong>
							<span>{{ row.description }}</span>
							<small>Target: {{ row.targetDisplayName }}</small>
						</button>
					</section>
				</nav>

				<section class="mapper-guide-playground" aria-label="Interactive grid feature playground">
					<div class="mapper-guide-demo-head">
						<div>
							<div class="section-kicker">Interactive playground</div>
							<h3>{{ selectedMapperGuideCaseDetail.title }}</h3>
							<p>{{ selectedMapperGuideCaseDetail.summary }}</p>
						</div>
						<div class="mapper-guide-demo-actions">
							<span>Demo-only: nothing here is saved</span>
							<button type="button" class="mapper-guide-reset-btn" (click)="resetSelectedMapperGuideRow()">Reset example</button>
						</div>
					</div>

					<div class="mapper-guide-existing-headers" aria-label="Relevant mapper headers">
						<div class="mapper-tabs mapper-guide-tabs">
							<button type="button" class="mapper-tab-btn" [class.active]="isMapperGuideHeaderActive('Create')">Create</button>
							<button type="button" class="mapper-tab-btn optional" [class.active]="isMapperGuideHeaderActive('Update')">
								<span>Update</span>
								<small>Optional</small>
							</button>
							<button type="button" class="mapper-tab-btn" [class.active]="isMapperGuideHeaderActive('Writeback')">Writeback</button>
							<button type="button" class="mapper-tab-btn" [class.active]="isMapperGuideHeaderActive('Messages')">Messages</button>
						</div>
					</div>

					<div class="mapper-guide-explanation-card">
						<div class="mapper-guide-howto">
							<div class="mapper-guide-explanation-title">How to use this case</div>
							<ol>
								<li *ngFor="let step of selectedMapperGuideCaseDetail.steps">{{ step }}</li>
							</ol>
						</div>
						<div class="mapper-guide-example-flow">
							<div><small>Example input</small><strong>{{ selectedMapperGuideCaseDetail.exampleInput }}</strong></div>
							<div><small>Expected result</small><strong>{{ selectedMapperGuideCaseDetail.exampleResult }}</strong></div>
						</div>
					</div>

					<div class="mapper-guide-grid-shell">
						<app-mapping-rules-grid [schema]="mapperGuideTargetSchema" [mappingRows]="selectedMapperGuideRows" [sourceFieldOptions]="mapperGuideSourceFieldOptions" [sourceTreeItems]="mapperGuideSourceTreeItems" [referenceFieldOptionsByEntity]="mapperGuideReferenceFieldOptionsByEntity"
							[selectedRowId]="selectedMapperGuideRowId" [sourceSystemName]="mapperGuideSourceSystemName" [targetSystemName]="mapperGuideTargetSystemName" [getRowState]="resolveMapperGuideRowState" [getModeDescription]="resolveMapperGuideModeDescription"
							(mappingModeChanged)="onMapperGuideModeChanged($event)" (previewRequested)="onMapperGuidePreviewRequested($event)" (expressionEditRequested)="onMapperGuideExpressionEditRequested($event)" (lookupEditRequested)="onMapperGuideLookupEditRequested($event)"
							(picklistMapRequested)="openMapperGuidePicklistMappingEditor($event)" (linkKeyChanged)="onMapperGuideLinkKeyChanged($event)" (rowSelected)="selectMapperGuideRow($event)">
						</app-mapping-rules-grid>
					</div>

					<div class="mapper-guide-marker-legend">
						<div class="mapper-guide-explanation-title">Grid icons and markers</div>
						<div class="mapper-guide-marker-list">
							<div class="mapper-guide-marker-item" *ngFor="let marker of mapperGuideMarkerLegend">
								<span class="mapper-guide-marker-icon" [ngClass]="marker.className" aria-hidden="true"></span>
								<div>
									<strong>{{ marker.label }}</strong>
									<span>{{ marker.description }}</span>
								</div>
							</div>
							<div class="mapper-guide-marker-item">
								<span class="mapper-guide-key-box" aria-hidden="true"></span>
								<div>
									<strong>Key checkbox</strong>
									<span>Tick this after mapping a stable field to make it the shared record match key.</span>
								</div>
							</div>
						</div>
					</div>

				</section>
			</div>
		</section>
	</div>
</div>
